/* Listens to lp-session-loaded and repaints the responses / synthesis /
 * optimal-prompt panels from the restored state. Lives separately from
 * session-io so that module stays focused on file I/O.
 *
 * The runner / synthesis / optimal-prompt modules each own their own DOM
 * and we don't import private renderers from them; instead we read state
 * after the snapshot has been applied and re-call public renderers via
 * minimal DOM rebuilds.
 */

import { state, savedState } from './state';
import { MODELS } from '../data/models';
import { renderMarkdown, mdToHtml } from './ui-helpers';
import { refreshChips } from './attachments';

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' } as any)[c]
  );
}

function rebuildResponses(): void {
  const panel = document.getElementById('responses-panel');
  const grid = document.getElementById('responses-grid');
  if (!panel || !grid) return;
  const responses = state.lastResponses || [];
  if (responses.length === 0) {
    panel.style.display = 'none';
    return;
  }
  panel.style.display = '';
  // Force runner's lazy DOM to reflect — clear + rebuild minimal cards.
  // Runner's full card lifecycle (animations, action menus) only fires on
  // run-paths; here we just paint the persisted content so the user sees
  // the loaded responses.
  grid.innerHTML = '';
  const cols = Math.min(responses.length, 4);
  grid.className = `responses-grid cols-${cols}`;
  responses.forEach((r: any) => {
    const m = MODELS.find((mm: any) => mm.id === r.modelId);
    const label = m?.label || r.modelId;
    const provider = m?.provider || '?';
    const card = document.createElement('article');
    card.className = `response-card response-card-${provider}` + (r.ok ? '' : ' response-card-error');
    const body = r.ok
      ? `<div class="response-body markdown">${mdToHtml(r.text || '')}</div>`
      : `<div class="response-error">${escapeHtml(r.error || 'failed')}</div>`;
    card.innerHTML = `
      <header class="response-head">
        <span class="response-badge response-badge-${provider}">${label}</span>
        <span class="response-meta">${r.durationMs ? `${(r.durationMs / 1000).toFixed(1)}s · ` : ''}${r.tokensOut || 0} tok${r.costUsd != null ? ` · $${r.costUsd.toFixed(4)}` : ''}</span>
      </header>
      ${body}
    `;
    grid.appendChild(card);
  });
}

function rebuildSynthesis(): void {
  const panel = document.getElementById('synth-panel');
  const out = document.getElementById('synth-output');
  const actions = document.getElementById('synth-actions');
  if (!panel || !out) return;
  // The synth panel is normally hidden until the runner reveals it (after
  // 2+ successful responses). On restore we have to reveal it ourselves
  // whenever there's any synth content to show — even if responses are
  // empty (e.g. the user saved a session post-synthesis and the responses
  // failed to deserialize for whatever reason).
  if (!savedState.lastSynthText) {
    out.className = 'synth-output empty';
    return;
  }
  panel.style.display = '';
  out.classList.remove('empty');
  out.innerHTML = `<div class="markdown">${mdToHtml(savedState.lastSynthText)}</div>`;
  if (actions) actions.style.display = '';

  // Restore the synth picker values from state (set by applySnapshot's
  // UI-pickers block, defensively re-applied here so the change events
  // run AFTER the restore event handler has hooked up).
  if (savedState.lastSynthMode) {
    const modeEl = document.getElementById('synth-mode') as HTMLSelectElement | null;
    if (modeEl && modeEl.value !== savedState.lastSynthMode) {
      modeEl.value = savedState.lastSynthMode;
      modeEl.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  if (savedState.lastSynthModelId) {
    const modelEl = document.getElementById('synth-model') as HTMLSelectElement | null;
    if (modelEl && modelEl.value !== savedState.lastSynthModelId) {
      modelEl.value = savedState.lastSynthModelId;
    }
  }
}

function rebuildDeliverables(): void {
  // Show a small list of what the user had exported in this session.
  // Below the synth-actions row so it sits next to the synth output.
  const synthPanel = document.getElementById('synth-panel');
  if (!synthPanel) return;
  let host = document.getElementById('restored-deliverables');
  if (host) host.remove();

  const list = (state as any).deliverables || [];
  if (!list.length) return;

  host = document.createElement('div');
  host.id = 'restored-deliverables';
  host.style.cssText =
    'margin: 14px 0 0; padding: 12px 14px; background: rgba(252,226,138,0.06);' +
    'border: 1px solid rgba(252,226,138,0.30); border-radius: 8px;' +
    'font-size: 12px; color: var(--on-dark-2);';
  host.innerHTML =
    `<div style="font-family:'JetBrains Mono',monospace; letter-spacing:0.12em; ` +
    `font-size:10.5px; color:var(--gold); margin-bottom:8px;">EXPORTS IN THIS SESSION</div>` +
    `<ul style="list-style:none; padding:0; margin:0;">` +
    list.map((d: any) => (
      `<li style="display:flex; gap:10px; align-items:center; padding:4px 0;">` +
        `<span style="font-family:'JetBrains Mono',monospace; font-size:10px; ` +
        `padding:2px 7px; background:rgba(252,226,138,0.10); border-radius:4px;` +
        `color:var(--gold); letter-spacing:0.08em;">${escapeHtml((d.kind || '').toUpperCase())}</span>` +
        `<span style="color:var(--on-dark);">${escapeHtml(d.name || '(unnamed)')}</span>` +
      `</li>`
    )).join('') +
    `</ul>`;
  synthPanel.appendChild(host);
}

function rebuildOptimal(): void {
  const panel = document.getElementById('optimal-panel');
  const out = document.getElementById('optimal-output') as HTMLTextAreaElement | null;
  const actions = document.getElementById('optimal-actions');
  if (!panel || !out) return;
  if (!savedState.lastOptimalPrompt) return;
  panel.style.display = '';
  out.value = savedState.lastOptimalPrompt;
  if (actions) actions.style.display = '';
}

export function attachSessionRestoreHandler(): void {
  window.addEventListener('lp-session-loaded', () => {
    rebuildResponses();
    rebuildSynthesis();
    rebuildDeliverables();
    rebuildOptimal();
    refreshChips();   // repaint attachment chips from the restored state
  });
}
